import type {
  StripeElements,
  PaymentMethod,
} from '@stripe/stripe-js';

import { logError } from '../AppInsights/Error/log-error';

import type {
  CreateNewPaymentRequest,
  CreatePaymentMethodResponse,
  PaymentMethodError,
} from './types';

const ERROR_CREATING_PAYMENT_METHOD =
  'Error creating payment method';

const focusFirstStripeValidationError = (
  elements: StripeElements,
  root: ParentNode = document,
) => {
  const paymentElementContainer =
    root.querySelector<HTMLElement>('#paymentElement');
  paymentElementContainer?.scrollIntoView?.({
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  });

  const paymentElement = elements.getElement('payment');
  if (!paymentElement) {
    return;
  }

  try {
    (
      paymentElement as {
        focus: (options?: {
          firstError?: boolean;
          id?: string;
        }) => void;
      }
    ).focus({ firstError: true });
  } catch {
    paymentElement.focus();
  }
};

const StripeApi = {
  focusFirstValidationError: focusFirstStripeValidationError,
  validatePaymentMethod: async ({
    elements,
    focusOnError = true,
    errorMessage = 'Form validation error',
    root,
  }: {
    elements: StripeElements;
    focusOnError?: boolean;
    errorMessage?: string;
    root?: ParentNode;
  }): Promise<{
    validationError?: PaymentMethodError;
  }> => {
    let error: PaymentMethodError | undefined;
    try {
      const stripeValidationResult = await elements.submit();

      if (stripeValidationResult?.error) {
        if (focusOnError) {
          focusFirstStripeValidationError(elements, root);
        }

        const { type, message = errorMessage } =
          stripeValidationResult.error;
        error = {
          errorType: type,
          message,
        };
      }
    } catch {
      error = {
        errorType: 'api_error',
        message: errorMessage,
      };
    }
    return { validationError: error };
  },
  createPaymentMethod: async ({
    elements,
    stripe,
    name,
    errorMessage = ERROR_CREATING_PAYMENT_METHOD,
  }: CreateNewPaymentRequest & {
    errorMessage?: string;
  }): Promise<CreatePaymentMethodResponse> => {
    let paymentMethod: PaymentMethod | undefined;
    let paymentMethodError: PaymentMethodError | undefined;

    if (!stripe || !elements) {
      throw new Error('Stripe API not available');
    }

    try {
      const paymentMethodResult =
        await stripe.createPaymentMethod({
          elements,
          params: {
            billing_details: {
              name,
            },
          },
        });

      paymentMethod = paymentMethodResult.paymentMethod;

      if (paymentMethodResult.error) {
        const {
          type,
          code = '',
          message = errorMessage,
        } = paymentMethodResult.error;

        paymentMethodError = {
          errorType: type,
          message,
        };

        logError({
          errorType: 'stripe_error',
          error: new Error(ERROR_CREATING_PAYMENT_METHOD),
          metadata: {
            type,
            code,
            message,
          },
        });
      }
    } catch {
      paymentMethodError = {
        errorType: 'api_error',
        message: errorMessage,
      };
    }
    return {
      paymentMethod,
      paymentMethodError,
    };
  },
};

export default StripeApi;
